import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import './PanelManager.scss';

export interface PanelPosition {
  x: number;
  y: number;
}

export interface PanelSize {
  width: number;
  height: number;
}

export interface PanelState {
  id: string;
  title: string;
  position: PanelPosition;
  size: PanelSize;
  isMinimized: boolean;
  isDocked: boolean;
  zIndex: number;
  preservedState?: any; // Generic state preservation for components
}

export interface DockablePanel extends PanelState {
  content: React.ReactNode | ((panelId: string) => React.ReactNode);
  onClose?: () => void;
  onMinimize?: () => void;
  onRestore?: () => void;
}

export interface PanelManagerContextValue {
  panels: DockablePanel[];
  addPanel: (panel: Omit<DockablePanel, 'id' | 'zIndex'>) => string;
  removePanel: (id: string) => void;
  updatePanel: (id: string, updates: Partial<PanelState>) => void;
  bringToFront: (id: string) => void;
  minimizePanel: (id: string) => void;
  restorePanel: (id: string) => void;
  getHighestZIndex: () => number;
  // State preservation methods
  preservePanelState: (id: string, state: any) => void;
  getPreservedState: (id: string) => any;
}

export const PanelManagerContext = React.createContext<PanelManagerContextValue | null>(null);

export const usePanelManager = (): PanelManagerContextValue => {
  const context = React.useContext(PanelManagerContext);
  if (!context) {
    throw new Error('usePanelManager must be used within a PanelManagerProvider');
  }
  return context;
};

interface PanelManagerProviderProps {
  children: React.ReactNode;
}

export const PanelManagerProvider: React.FC<PanelManagerProviderProps> = ({ children }) => {
  const [panels, setPanels] = useState<DockablePanel[]>([]);
  const nextIdRef = useRef(1);
  const nextZIndexRef = useRef(1000);

  const addPanel = useCallback((panelData: Omit<DockablePanel, 'id' | 'zIndex'>): string => {
    const id = `panel-${nextIdRef.current++}`;
    const zIndex = nextZIndexRef.current++;
    
    const newPanel: DockablePanel = {
      ...panelData,
      id,
      zIndex
    };

    setPanels(prev => [...prev, newPanel]);
    return id;
  }, []);

  const removePanel = useCallback((id: string) => {
    setPanels(prev => prev.filter(panel => panel.id !== id));
  }, []);

  const updatePanel = useCallback((id: string, updates: Partial<PanelState>) => {
    setPanels(prev => prev.map(panel => 
      panel.id === id ? { ...panel, ...updates } : panel
    ));
  }, []);

  const bringToFront = useCallback((id: string) => {
    const newZIndex = nextZIndexRef.current++;
    updatePanel(id, { zIndex: newZIndex });
  }, [updatePanel]);

  const minimizePanel = useCallback((id: string) => {
    updatePanel(id, { isMinimized: true });
  }, [updatePanel]);

  const restorePanel = useCallback((id: string) => {
    updatePanel(id, { isMinimized: false });
  }, [updatePanel]);

  const getHighestZIndex = useCallback(() => {
    return nextZIndexRef.current;
  }, []);

  const preservePanelState = useCallback((id: string, state: any) => {
    console.log('Preserving panel state for', id, state);
    updatePanel(id, { preservedState: state });
  }, [updatePanel]);

  const getPreservedState = useCallback((id: string) => {
    const panel = panels.find(p => p.id === id);
    return panel?.preservedState || null;
  }, [panels]);

  const contextValue: PanelManagerContextValue = {
    panels,
    addPanel,
    removePanel,
    updatePanel,
    bringToFront,
    minimizePanel,
    restorePanel,
    getHighestZIndex,
    preservePanelState,
    getPreservedState
  };

  return (
    <PanelManagerContext.Provider value={contextValue}>
      {children}
      <PanelRenderer />
    </PanelManagerContext.Provider>
  );
};

const PanelRenderer: React.FC = () => {
  const { panels } = usePanelManager();
  
  // Render panels in a portal to ensure they appear above everything else
  return createPortal(
    <>
      {panels.map(panel => (
        <DockablePanelWindow key={panel.id} panel={panel} />
      ))}
    </>,
    document.body
  );
};

interface DockablePanelWindowProps {
  panel: DockablePanel;
}

const DockablePanelWindow: React.FC<DockablePanelWindowProps> = ({ panel }) => {
  const { updatePanel, bringToFront, removePanel, minimizePanel, restorePanel } = usePanelManager();
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number; panelX: number; panelY: number } | null>(null);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.panel-resize-handle')) {
      return; // Let resize handle deal with this
    }
    
    // Always bring panel to front when clicked anywhere
    bringToFront(panel.id);
    
    // Only start dragging if the mousedown is on the header
    if ((e.target as HTMLElement).closest('.panel-header')) {
      console.log('Panel drag started on header', panel.id);
      e.preventDefault();
      e.stopPropagation();
      
      isDraggingRef.current = true;
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panelX: panel.position.x,
        panelY: panel.position.y
      };
      
      // Add a class to the body to indicate dragging is happening
      document.body.classList.add('panel-dragging');
    }
  }, [panel.id, panel.position, bringToFront]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    console.log('Panel resize started', panel.id);
    e.preventDefault();
    e.stopPropagation();
    
    bringToFront(panel.id);
    
    // Record initial state for resize
    isResizingRef.current = true;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: panel.size.width,
      height: panel.size.height
    };
    
    // Add resize visual indicator
    document.body.classList.add('panel-resizing');
    
    // Add active resize class to the panel for visual feedback
    const panelElement = (e.target as HTMLElement).closest('.dockable-panel');
    if (panelElement) {
      panelElement.classList.add('panel-resizing-active');
    }
    
    // Add event listeners immediately for resize operation
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizingRef.current || !resizeStartRef.current) return;
      
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      
      const deltaX = moveEvent.clientX - resizeStartRef.current.x;
      const deltaY = moveEvent.clientY - resizeStartRef.current.y;
      
      // Enforce minimum size and max size based on window dimensions
      const newWidth = Math.max(300, Math.min(window.innerWidth - panel.position.x - 20, resizeStartRef.current.width + deltaX));
      const newHeight = Math.max(200, Math.min(window.innerHeight - panel.position.y - 20, resizeStartRef.current.height + deltaY));
      
      console.log('Resizing panel to:', newWidth, 'x', newHeight);
      
      updatePanel(panel.id, {
        size: {
          width: newWidth,
          height: newHeight
        }
      });
    };
    
    const handleMouseUp = (upEvent: MouseEvent) => {
      console.log('Panel resize ended', panel.id);
      upEvent.preventDefault();
      upEvent.stopPropagation();
      
      // Clean up resize state
      isResizingRef.current = false;
      resizeStartRef.current = null;
      
      // Remove classes
      document.body.classList.remove('panel-resizing');
      document.querySelectorAll('.panel-resizing-active').forEach(el => {
        el.classList.remove('panel-resizing-active');
      });
      
      // Remove event listeners
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
    };
    
    // Add event listeners with capture to ensure they're handled before other handlers
    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
    
  }, [panel.id, panel.size, panel.position, bringToFront, updatePanel]);

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current && dragStartRef.current) {
        console.log('Panel drag moving', panel.id, 'dx:', e.clientX - dragStartRef.current.x, 'dy:', e.clientY - dragStartRef.current.y);
        e.preventDefault();
        e.stopPropagation();
        
        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        
        // Calculate new position with bounds checking
        const newX = Math.max(0, Math.min(window.innerWidth - 100, dragStartRef.current.panelX + deltaX));
        const newY = Math.max(0, Math.min(window.innerHeight - 100, dragStartRef.current.panelY + deltaY));
        
        updatePanel(panel.id, {
          position: {
            x: newX,
            y: newY
          }
        });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        console.log('Panel drag ended', panel.id);
        e.preventDefault();
        e.stopPropagation();
        
        // Remove classes from body
        document.body.classList.remove('panel-dragging');
      }
      
      isDraggingRef.current = false;
      dragStartRef.current = null;
    };

    if (isDraggingRef.current) {
      // Use capture phase for more reliable event handling
      document.addEventListener('mousemove', handleMouseMove, { capture: true });
      document.addEventListener('mouseup', handleMouseUp, { capture: true });
      
      // Also listen for ESC key to cancel dragging
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          console.log('Panel drag canceled via ESC key');
          isDraggingRef.current = false;
          dragStartRef.current = null;
          document.body.classList.remove('panel-dragging');
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove, { capture: true });
        document.removeEventListener('mouseup', handleMouseUp, { capture: true });
        document.removeEventListener('keydown', handleKeyDown);
        document.body.classList.remove('panel-dragging');
      };
    }

    return () => {}; // No cleanup needed when not dragging
  }, [panel.id, updatePanel]);

  const handleClose = useCallback(() => {
    panel.onClose?.();
    removePanel(panel.id);
  }, [panel.id, panel.onClose, removePanel]);

  const handleMinimize = useCallback(() => {
    panel.onMinimize?.();
    minimizePanel(panel.id);
  }, [panel.id, panel.onMinimize, minimizePanel]);

  const handleRestore = useCallback(() => {
    panel.onRestore?.();
    restorePanel(panel.id);
  }, [panel.id, panel.onRestore, restorePanel]);

  if (panel.isMinimized) {
    return (
      <div
        className="dockable-panel-minimized"
        style={{
          position: 'fixed',
          bottom: 20,
          left: panel.position.x,
          zIndex: panel.zIndex,
        }}
        onClick={handleRestore}
      >
        {panel.title}
      </div>
    );
  }

  return (
    <div
      className="dockable-panel"
      style={{
        position: 'fixed',
        left: panel.position.x,
        top: panel.position.y,
        width: panel.size.width,
        height: panel.size.height,
        zIndex: panel.zIndex,
        backgroundColor: '#ffffff',
        border: '1px solid #ccc',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Panel Header */}
      <div className="panel-header">
        <h3>{panel.title}</h3>
        <div className="panel-controls">
          <button
            onClick={handleMinimize}
            className="panel-minimize-btn"
            title="Minimize panel"
            aria-label="Minimize panel"
          >
            <FontAwesomeIcon icon={faCaretDown} />
          </button>
          <button
            onClick={handleClose}
            className="panel-close-btn"
            title="Close panel"
            aria-label="Close panel"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {/* Panel Content */}
      <div className="panel-content">
        {typeof panel.content === 'function' ? panel.content(panel.id) : panel.content}
      </div>

      {/* Resize Handle */}
      <div
        className="panel-resize-handle"
        onMouseDown={handleResizeStart}
        title="Resize panel"
        aria-label="Resize panel"
      >
        <span className="sr-only">Resize panel</span>
      </div>
    </div>
  );
};
